import { client, log } from ".";
import { getDesglosePorEdificio } from "./comisionEdificiosUtil";

/**
 * Carga los denominadores congelados del snapshot para un país, en un Map keyed por la misma
 * clave que `keyDeFactura` ('s:{sale_id}' / 'c:{contrato_id}|{mes}'). Lo consumen los motores
 * (Paso 5) para dividir por la foto en vez de `pantallastotales`. Una sola query, país-scopeada.
 * Aún NO lo usa ningún motor (andamiaje del Paso 5).
 */
export const cargarSnapshotDenominadores = async (pais) => {
  const { data, error } = await client
    .from("comision_snapshot")
    .select("origen_tipo, sale_id, contrato_id, mes, denominador_total")
    .eq("pais", pais);
  if (error) throw error;
  const map = new Map();
  for (const r of data || []) {
    const key = r.origen_tipo === "sale" ? `s:${r.sale_id}` : `c:${r.contrato_id}|${r.mes}`;
    map.set(key, r.denominador_total);
  }
  return map;
};

/**
 * Captura (o re-captura) la foto de comisión de una venta o mes-de-contrato.
 *
 * Congela el desglose de pantallas por edificio (4 circuitos) en las tablas
 * comision_snapshot (padre) + comision_snapshot_edificio (hijos), de modo que
 * editar el inventario de un edificio NO mueva la comisión histórica (deuda 9.19).
 *
 * Idempotente: si ya existe foto vigente para ese sale_id o (contrato_id, mes),
 * la reemplaza (borra la anterior — CASCADE borra hijos — y reescribe). Soporta
 * 'creacion', 'modificacion' y futura re-captura sin duplicar (respeta el UNIQUE parcial).
 *
 * Corre con el rol authenticated (la policy "Authenticated all" lo permite).
 * NO lee ni toca sales/sales_contratos/comisiones — solo escribe las tablas snapshot.
 *
 * @param {Object} p
 * @param {'sale'|'contrato_mes'} p.origen_tipo
 * @param {number} [p.sale_id]       requerido si origen_tipo='sale'
 * @param {number} [p.contrato_id]   requerido si origen_tipo='contrato_mes'
 * @param {string} [p.mes]           "MM-YYYY", requerido si origen_tipo='contrato_mes'
 * @param {Array}  p.edificios       objetos edificio participantes (con pantallas/totems/espera/cowork)
 * @param {Object} p.csvs            { pantallas_ascensores, pantallas_circulacion, pantallas_espera, pantallas_cowork }
 * @param {number} [p.monto]         importe congelado de la venta/mes
 * @param {string} p.pais
 * @param {'creacion'|'modificacion'|'backfill'} p.motivo
 */
export const capturarSnapshotComision = async ({
  origen_tipo,
  sale_id = null,
  contrato_id = null,
  mes = null,
  edificios = [],
  csvs = {},
  monto = null,
  pais,
  motivo,
}) => {
  try {
    // 1. Desglose por circuito de cada edificio participante (hijos)
    const hijos = (edificios || []).map((e) => ({
      edificio_id: e.id,
      ...getDesglosePorEdificio(e, csvs),
    }));

    // 2. Invariante: el denominador del padre es la suma de los 4 circuitos de los hijos
    const denominador_total = hijos.reduce(
      (acc, h) => acc + h.pantallas_asc + h.pantallas_circ + h.pantallas_esp + h.pantallas_cow,
      0
    );

    // 3. Idempotencia: borrar la foto vigente de esta clave (CASCADE borra los hijos)
    let delQ = client.from("comision_snapshot").delete().eq("origen_tipo", origen_tipo);
    delQ = origen_tipo === "sale" ? delQ.eq("sale_id", sale_id) : delQ.eq("contrato_id", contrato_id).eq("mes", mes);
    const { error: delErr } = await delQ;
    if (delErr) throw delErr;

    // 4. Insertar el padre y obtener su id
    const { data: padre, error: padreErr } = await client
      .from("comision_snapshot")
      .insert({
        origen_tipo,
        sale_id,
        contrato_id,
        mes,
        denominador_total,
        monto_congelado: monto,
        pais,
        motivo_captura: motivo,
      })
      .select("id")
      .single();
    if (padreErr) throw padreErr;

    // 5. Insertar los hijos
    if (hijos.length > 0) {
      const { error: hijosErr } = await client
        .from("comision_snapshot_edificio")
        .insert(hijos.map((h) => ({ snapshot_id: padre.id, ...h })));
      if (hijosErr) throw hijosErr;
    }

    return { snapshot_id: padre.id, denominador_total, hijos: hijos.length };
  } catch (error) {
    log.error("capturarSnapshotComision", error);
    throw error;
  }
};

// Trae los edificios vivos (sus 4 circuitos) por lista de IDs.
const fetchEdificiosPorIds = async (ids) => {
  if (ids.length === 0) return [];
  const { data, error } = await client.from("edificios").select("id, pantallas, totems, espera, cowork").in("id", ids);
  if (error) throw error;
  return data || [];
};

const parseIds = (csv) =>
  (csv || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Orquesta la captura del snapshot a partir de una fila de `sales` recién creada/editada.
 * Toda venta con edificios participantes nace con foto. Sin CSV de circuitos (carga manual
 * Perú) → `getDesglosePorEdificio` usa la rama `camposVacios` (4 circuitos del edificio),
 * mismo criterio que el backfill (CABO 1). Solo se saltea si no hay edificios participantes.
 */
export const capturarSnapshotDesdeVenta = async (venta, motivo = "creacion") => {
  const csvs = {
    pantallas_ascensores: venta.pantallas_ascensores,
    pantallas_circulacion: venta.pantallas_circulacion,
    pantallas_espera: venta.pantallas_espera,
    pantallas_cowork: venta.pantallas_cowork,
  };

  const ids = parseIds(venta.edificiosparticipantes);
  if (ids.length === 0) return null;
  const edificios = await fetchEdificiosPorIds(ids);

  return capturarSnapshotComision({
    origen_tipo: "sale",
    sale_id: venta.id,
    edificios,
    csvs,
    monto: venta.importe,
    pais: venta.pais,
    motivo,
  });
};

/**
 * Orquesta la captura de un contrato: N fotos, UNA por mes, cada una con los edificios
 * y los 4 CSV de ESE mes (no los del contrato entero). Keyed por (contrato_id, mes).
 */
export const capturarSnapshotDesdeContrato = async (contrato, motivo = "creacion") => {
  const meses = contrato.meses || [];
  const allIds = [...new Set(meses.flatMap((m) => parseIds(m.edificios_ids)))];
  if (allIds.length === 0) return [];

  const edifList = await fetchEdificiosPorIds(allIds);
  const edifMap = Object.fromEntries(edifList.map((e) => [String(e.id), e]));

  const results = [];
  for (const m of meses) {
    const ids = parseIds(m.edificios_ids);
    if (ids.length === 0) continue;
    const edificios = ids.map((id) => edifMap[id]).filter(Boolean);
    const csvs = {
      pantallas_ascensores: m.pantallas_ascensores,
      pantallas_circulacion: m.pantallas_circulacion,
      pantallas_espera: m.pantallas_espera,
      pantallas_cowork: m.pantallas_cowork,
    };
    // eslint-disable-next-line no-await-in-loop
    const r = await capturarSnapshotComision({
      origen_tipo: "contrato_mes",
      contrato_id: contrato.id,
      mes: m.mes,
      edificios,
      csvs,
      monto: m.importe,
      pais: contrato.pais,
      motivo,
    });
    results.push(r);
  }
  return results;
};

/**
 * Borra las fotos de meses que ya NO existen en el contrato (CABO 2: evita huérfanas).
 * Al eliminar un mes de un `sales_contratos`, su foto `contrato_mes` quedaría colgada
 * (la re-captura solo toca meses presentes, no borra los ausentes). CASCADE borra los hijos.
 * Aditivo: el caller la envuelve en try/catch para no abortar el update.
 */
export const eliminarSnapshotsContratoMeses = async (contrato_id, meses) => {
  if (!contrato_id || !meses || meses.length === 0) return;
  const { error } = await client
    .from("comision_snapshot")
    .delete()
    .eq("origen_tipo", "contrato_mes")
    .eq("contrato_id", contrato_id)
    .in("mes", meses);
  if (error) throw error;
};

// ── Flag de sensibilidad: qué cambios de una venta/mes obligan a re-capturar la foto ──
// SOLO estos campos mueven la proporción o el monto del snapshot.
export const CAMPOS_SENSIBLES_VENTA = [
  "edificiosparticipantes",
  "pantallas_ascensores",
  "pantallas_circulacion",
  "pantallas_espera",
  "pantallas_cowork",
  "pantallastotales",
  "importe",
];

// Equivalente a nivel de cada mes de un contrato.
export const CAMPOS_SENSIBLES_MES = [
  "edificios_ids",
  "pantallas_ascensores",
  "pantallas_circulacion",
  "pantallas_espera",
  "pantallas_cowork",
  "pantallas_totales",
  "importe",
];

/**
 * true SOLO si cambió algún campo sensible (proporción/monto) entre antes y después.
 * Inocuos (NO disparan): facturanumero/facturafecha/facturafechacobro/plazocobro/
 * fecha_cobro_estimada/otros_costos/eliminada/intermediario_override_id/estatus/etc.
 * Comparación estricta: nunca omite un cambio real (puede re-capturar de más ante
 * diferencias de tipo, que es el lado seguro — la captura es idempotente).
 */
export const cambioRequiereRecaptura = (antes, despues, campos = CAMPOS_SENSIBLES_VENTA) =>
  !!antes && !!despues && campos.some((c) => (antes[c] ?? null) !== (despues[c] ?? null));
